import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, AlertTriangle, Pause } from 'lucide-react';

interface TimerComponentProps {
  totalTime: number; // in seconds
  onTimeUp: () => void;
  onTimeUpdate: (timeRemaining: number) => void;
  isPaused?: boolean;
  showWarnings?: boolean;
}

const TimerComponent: React.FC<TimerComponentProps> = ({
  totalTime,
  onTimeUp,
  onTimeUpdate,
  isPaused = false,
  showWarnings = true,
}) => {
  const [timeRemaining, setTimeRemaining] = useState(totalTime);
  const [isWarning, setIsWarning] = useState(false);
  const [isCritical, setIsCritical] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;

        if (newTime <= 0) {
          // Use setTimeout to avoid state update during render
          setTimeout(() => onTimeUp(), 0);
          return 0;
        }

        // Use setTimeout to avoid state update during render
        setTimeout(() => onTimeUpdate(newTime), 0);

        // Set warning states
        const percentage = (newTime / totalTime) * 100;
        setIsWarning(percentage <= 25 && percentage > 10);
        setIsCritical(percentage <= 10);

        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isPaused, totalTime, onTimeUp, onTimeUpdate]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (isCritical) return 'from-red-500 to-pink-500';
    if (isWarning) return 'from-yellow-500 to-orange-500';
    return 'from-blue-500 to-cyan-500';
  };

  const getBackgroundColor = () => {
    if (isCritical) return 'from-red-50 to-pink-50 border-red-200';
    if (isWarning) return 'from-yellow-50 to-orange-50 border-yellow-200';
    return 'from-blue-50 to-cyan-50 border-blue-200';
  };

  const percentage = (timeRemaining / totalTime) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{
        opacity: 1,
        scale: 1,
        ...(isCritical && { scale: [1, 1.05, 1] }),
      }}
      transition={{
        duration: 0.3,
        ...(isCritical && { repeat: Infinity, duration: 1 }),
      }}
      className={`bg-gradient-to-r ${getBackgroundColor()} rounded-2xl p-4 border-2 shadow-lg`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <motion.div
            animate={isCritical ? { rotate: [0, -10, 10, 0] } : {}}
            transition={{ duration: 0.5, repeat: isCritical ? Infinity : 0 }}
            className={`w-10 h-10 bg-gradient-to-r ${getTimerColor()} rounded-full flex items-center justify-center`}
          >
            {isCritical ? (
              <AlertTriangle size={20} className="text-white" />
            ) : isPaused ? (
              <Pause size={20} className="text-white" />
            ) : (
              <Clock size={20} className="text-white" />
            )}
          </motion.div>

          <div>
            <div className="text-xs font-medium text-gray-600 uppercase tracking-wide">
              {isPaused ? 'Paused' : 'Time Remaining'}
            </div>
            <motion.div
              key={timeRemaining}
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              className={`text-2xl font-bold bg-gradient-to-r ${getTimerColor()} bg-clip-text text-transparent`}
            >
              {formatTime(timeRemaining)}
            </motion.div>
          </div>
        </div>

        {/* Circular Progress */}
        <div className="relative w-16 h-16">
          <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
            {/* Background circle */}
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              className="text-gray-200"
            />
            {/* Progress circle */}
            <motion.circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              strokeWidth="4"
              strokeLinecap="round"
              className={`${
                isCritical
                  ? 'text-red-500'
                  : isWarning
                    ? 'text-yellow-500'
                    : 'text-blue-500'
              }`}
              initial={{ strokeDasharray: '0 176' }}
              animate={{
                strokeDasharray: `${(percentage / 100) * 176} 176`,
                stroke: isCritical
                  ? '#ef4444'
                  : isWarning
                    ? '#f59e0b'
                    : '#3b82f6',
              }}
              transition={{ duration: 0.5 }}
            />
          </svg>

          {/* Percentage text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className={`text-sm font-bold ${
                isCritical
                  ? 'text-red-600'
                  : isWarning
                    ? 'text-yellow-600'
                    : 'text-blue-600'
              }`}
            >
              {Math.round(percentage)}%
            </span>
          </div>
        </div>
      </div>

      {/* Warning Messages */}
      {showWarnings && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{
            opacity: isWarning || isCritical ? 1 : 0,
            height: isWarning || isCritical ? 'auto' : 0,
          }}
          className="mt-3 overflow-hidden"
        >
          {isCritical && (
            <div className="flex items-center gap-2 text-red-600 text-sm font-medium">
              <AlertTriangle size={16} />
              Critical: Less than 10% time remaining!
            </div>
          )}
          {isWarning && !isCritical && (
            <div className="flex items-center gap-2 text-yellow-600 text-sm font-medium">
              <Clock size={16} />
              Warning: 25% time remaining
            </div>
          )}
        </motion.div>
      )}

      {/* Progress Bar */}
      <div className="mt-3">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <motion.div
            className={`bg-gradient-to-r ${getTimerColor()} h-2 rounded-full`}
            initial={{ width: '100%' }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default TimerComponent;
